import {
  AuthenticationError,
  calculateCodeChallenge,
  extractAuthenticationResponse,
  formatAuthenticationRequest,
  formatTokenRequest,
  parseIdTokenClaims,
  parseProviderConfig,
  parseTokenResponse,
  requiresInteraction,
} from './auth.js';
import {LocalKey, SessionKey, Store} from './store.js';
import {getRelativeUrl} from './util.js';
import {hash, randomBytes} from './encoding.js';

const codeChallengeMethod = 'S256';
const grantType = 'authorization_code';
const maxRenewMargin = 5 * 60 * 1000; // max time ahead of expiry to initiate token renewal in milliseconds
const minRenewMargin = 2 * 60 * 1000; // min time ahead of expiry to initiate token renewal in milliseconds

/**
 * @typedef {Object} User
 * @property {import('./auth.js').IdTokenClaims} claims The claims from the ID token.
 * @property {string} idToken The ID token.
 * @property {string} accessToken The access token.
 * @property {number} expiry The expiry time of the access token (in milliseconds since the epoch).
 */

/**
 * @typedef {Object} State
 * @property {boolean} pending The authorization code flow is in progress.
 * @property {User|null} user The user info.
 * @property {Error|null} error Any error (or null if none).
 */

/**
 * @typedef {Object} RenewMessage
 * @property {'renew'} type The message type.
 * @property {number} expiry The expiry time of the tokens (in milliseconds since the epoch).
 */

/**
 * @typedef {Object} SignOutMessage
 * @property {'signOut'} type The message type.
 */

/**
 * @typedef {Object} ErrorMessage
 * @property {'error'} type The message type.
 * @property {string} message The error message.
 * @property {string} name The name for the type of error.
 * @property {boolean} renew The error occurred during token renewal.
 * @property {string} [stack] The error stack.
 * @property {string} [code] Any error code.
 */

/**
 * @typedef {RenewMessage|SignOutMessage|ErrorMessage} MessageData
 */

/**
 * @typedef {function(State): void} ChangeListener
 */

/**
 * @typedef {function(string): void} Navigate
 */

/**
 * Determine if the current window is a top-level window.
 * @return {boolean} The current window is at the top level.
 */
export function isTopLevel() {
  try {
    return window.self === window.top;
  } catch {
    return false;
  }
}

/**
 * @typedef {Object} Log
 * @property {function(...any): void} debug Log a message at the debug level.
 * @property {function(...any): void} info Log a message at the info level.
 * @property {function(...any): void} warn Log a message at the warn level.
 * @property {function(...any): void} error Log a message at the error level.
 */

/**
 * @type {Log}
 */
const defaultLog = {
  debug() {},
  info() {},
  warn() {},
  error(...data) {
    console.error(...data); // eslint-disable-line no-console
  },
};

/**
 * @param {string} message A message.
 */
function prefix(message) {
  return 'auth: ' + message;
}

/**
 * @typedef {Object} ClientOptions
 * @property {string} clientId The client ID.
 * @property {string} [redirectUri] The redirect URL (defaults to `window.location.origin`).
 * @property {string} [postLogoutRedirectUri] Optional post-logout redirect URL.
 * @property {string} [issuer] The issuer URL.
 * @property {Array<string>} [scopes] The scopes to request.
 * @property {Navigate} [navigate] Optional function used to navigate back to the original URL after login.
 * @property {Log} [log] Optional logging interface (e.g. `console`).  By default, only error level messages are logged.
 * @property {import('./auth.js').AuthenticationParams} [params] Optional extra parameters to be included in the authentication request.
 */

/**
 * @typedef {Object} ClientConfig
 * @property {string} clientId The client ID.
 * @property {string} redirectUri The redirect URL.
 * @property {string} [postLogoutRedirectUri] The post-logout redirect URL.
 * @property {string} issuer The issuer URL.
 * @property {Array<string>} scopes The scopes to request.
 * @property {Navigate} navigate Function used to navigate back to the original URL after login.
 * @property {Log} log Logging interface.
 * @property {import('./auth.js').AuthenticationParams} [params] Optional extra parameters to be included in the authentication request.
 */

/**
 * @type {Navigate}
 */
const defaultNavigate = href => window.location.assign(href);

/**
 * @typedef {Object} Issuers
 * @property {string} production The production issuer URL.
 * @property {string} staging The staging issuer URL.
 */

/**
 * @type {Issuers}
 */
export const issuers = {
  production: 'https://account.planet.com/oauth2/planet',
  staging: 'https://account-next.planet.com/oauth2/planet',
};

/**
 * @type {Issuers}
 */
export const futureIssuers = {
  production: 'https://login.planet.com/',
  staging: 'https://login-next.planet.com/',
};

/**
 * @param {ClientOptions} options The client options.
 * @return {ClientConfig} The client configuration with defaults applied.
 */
export function applyOptions(options) {
  if (!options.clientId) {
    throw new Error('clientId is required');
  }

  if (options.scopes && !options.scopes.includes('openid')) {
    throw new Error('openid scope is required');
  }

  return {
    issuer: options.issuer || issuers.production,
    clientId: options.clientId,
    redirectUri: options.redirectUri || window.location.origin,
    postLogoutRedirectUri: options.postLogoutRedirectUri,
    scopes: options.scopes || ['openid', 'email', 'profile'],
    navigate: options.navigate || defaultNavigate,
    log: options.log || defaultLog,
    params: options.params,
  };
}

/**
 * Increment this when the structure of stored data changes or
 * to force previously stored data to be ignored.
 * @type {number}
 */
const version = 1;

/**
 * @param {ClientConfig} config The client configuration.
 * @return {string} A key used for storage and broadcast channel.
 */
export function getClientKey(config) {
  if (!config.scopes) {
    throw new Error('scopes is required');
  }
  if (!config.issuer) {
    throw new Error('issuer is required');
  }
  const scopes = config.scopes.slice().sort().join(',');
  return (
    'planet-auth-' +
    hash(`${config.issuer}|${config.clientId}|${scopes}`, version)
  );
}

export class Client {
  /**
   * The OAuth 2 client identifier.
   * @type {string}
   */
  #clientId;

  /**
   * The OpenID Connect issuer URL.
   * @type {string}
   */
  #issuer;

  /**
   * The OAuth 2 redirect URL for authorization requests.
   * @type {string}
   */
  #redirectUri;

  /**
   * Optional redirect URI sent to the end session endpoint.
   * @type {string|undefined}
   */
  #postLogoutRedirectUri;

  /**
   * A list of OAuth 2 scopes to request.
   * @type {Array<string>}
   */
  #scopes;

  /**
   * Store for state relevant to the current session.
   * @type {Store}
   */
  #sessionStore;

  /**
   * Store for state that can be shared between applications.
   * @type {Store}
   */
  #localStore;

  /**
   * An iframe used by the top level window for renweing tokens.
   * @type {HTMLIFrameElement|null}
   */
  #iframe = null;

  /**
   * The identifier of the timeout used to renew tokens.
   * @type {ReturnType<typeof setTimeout>}
   */
  #renewTimeoutId;

  /**
   * @type {Array<ChangeListener>}
   */
  #listeners;

  /**
   * Called to navigate back to the original URL after login.
   * @type {Navigate}
   */
  #navigate;

  /**
   * The authorization code flow is in progress.
   * @type {boolean}
   */
  #pending = false;

  /**
   * The most recent error (or null if none).
   * @type {Error|null}
   */
  #error = null;

  /**
   * A channel for communicating between contexts.
   * @type {BroadcastChannel}
   */
  #channel;

  /**
   * @type {Log}
   */
  #log;

  /**
   * @type {import('./auth.js').AuthenticationParams|undefined}
   */
  #extraParams;

  /**
   * @param {ClientOptions} options The client options.
   */
  constructor(options) {
    const config = applyOptions(options);

    this.#clientId = config.clientId;
    this.#issuer = config.issuer;
    this.#redirectUri = config.redirectUri;
    this.#postLogoutRedirectUri = config.postLogoutRedirectUri;
    this.#scopes = config.scopes;
    this.#navigate = config.navigate;
    this.#log = config.log;
    this.#extraParams = config.params;

    this.#listeners = [];

    const key = getClientKey(config);
    this.#sessionStore = new Store(key, sessionStorage);
    this.#localStore = new Store(key, localStorage);
    this.#channel = new BroadcastChannel(key);

    let handlingSignIn = false;
    let handlingRenew = false;
    const topLevel = isTopLevel();
    if (topLevel) {
      // messages from all contexts
      this.#channel.addEventListener('message', this.#handleMessage.bind(this));

      // messages from iframe owned by top level window
      window.addEventListener('message', this.#handleMessage.bind(this));

      const user = this.#getUser();
      if (user) {
        this.#scheduleRenew(user.expiry);
      }

      handlingSignIn = !!this.#sessionStore.extract(
        SessionKey.AWAITING_SIGN_IN,
      );
    } else {
      handlingRenew = !!this.#sessionStore.extract(SessionKey.AWAITING_RENEW);
    }

    this.#log.debug(prefix('initialized client'), {
      topLevel,
      handlingSignIn,
      handlingRenew,
    });

    if (
      (handlingSignIn || handlingRenew) &&
      this.#matchesRedirectUri(window.location.href)
    ) {
      this.#handleRedirect(handlingRenew).catch(err =>
        this.#handleError(asError(err)),
      );
    }
  }

  /**
   * Add a listener for changes to client state (user info, pending status, and any error).
   * @param {ChangeListener} listener The listener to add.
   */
  addChangeListener(listener) {
    this.#listeners.push(listener);
  }

  /**
   * Remove a change listener.
   * @param {ChangeListener} listener The listener to remove.
   */
  removeChangeListener(listener) {
    const index = this.#listeners.indexOf(listener);
    if (index !== -1) {
      this.#listeners.splice(index, 1);
    }
  }

  /**
   * Set the navigate function.
   * @param {Navigate} navigate The navigate function.
   */
  setNavigate(navigate) {
    this.#navigate = navigate;
  }

  /**
   * Dispatch a change event to all listeners.
   */
  #dispatchChange() {
    const status = this.getState();
    this.#log.debug(prefix('change'), status);

    for (const listener of this.#listeners) {
      try {
        listener(status);
      } catch {
        // ignore errors from listeners
      }
    }
  }

  /**
   * Get the OpenID provider configuration from the "well-known" endpoint.
   * @return {Promise<import('./auth.js').ProviderConfig>} The provider configuration.
   */
  async #getProviderConfig() {
    const configs = this.#sessionStore.get(SessionKey.PROVIDER_CONFIGS) || {};
    if (this.#issuer in configs) {
      return configs[this.#issuer];
    }

    const url = new URL(this.#issuer);
    const configPath = '.well-known/openid-configuration';
    url.pathname = `${url.pathname}/${configPath}`.replace('//', '/');

    this.#log.debug(prefix('requesting provider configuration'));
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Unable to get provider configuration from ${url}`);
    }

    const config = parseProviderConfig(await response.json());

    if (config.issuer !== this.#issuer) {
      throw new Error(
        `Issuer mismatch: expected ${this.#issuer}, got ${config.issuer}`,
      );
    }

    if (!config.codeChallengeMethodsSupported.includes(codeChallengeMethod)) {
      throw new Error(
        `The provider does not support the ${codeChallengeMethod} code challenge method.`,
      );
    }

    configs[this.#issuer] = config;
    this.#sessionStore.set(SessionKey.PROVIDER_CONFIGS, configs);
    return config;
  }

  /**
   * @return {User|null} The current user info.
   */
  #getUser() {
    const user = this.#localStore.get(LocalKey.USER);
    if (!user) {
      return null;
    }
    if (user.expiry < Date.now()) {
      this.#localStore.delete(LocalKey.USER);
      return null;
    }
    return user;
  }

  /**
   * Get the current state.
   * @return {State} The user info and pending status.
   */
  getState() {
    return {
      user: this.#getUser(),
      pending: this.#pending,
      error: this.#error,
    };
  }

  /**
   * Log out the current user.
   * @return {Promise<void>} A promise that resolves when the sign out flow is initiated.
   */
  signOut() {
    /**
     * @type {SignOutMessage}
     */
    const data = {type: 'signOut'};
    this.#channel.postMessage(data);
    return this.#signOut();
  }

  async #signOut() {
    const user = this.#localStore.get(LocalKey.USER);
    this.#log.debug(prefix('sign out'), {user});

    if (!user) {
      return;
    }

    const providerConfig = await this.#getProviderConfig();
    this.#localStore.clear();
    this.#sessionStore.clear();
    clearTimeout(this.#renewTimeoutId);

    if (!user || !providerConfig.endSessionEndpoint) {
      this.#dispatchChange();
      return;
    }

    const url = new URL(providerConfig.endSessionEndpoint);
    const params = url.searchParams;
    params.set('id_token_hint', user.idToken);
    if (this.#postLogoutRedirectUri) {
      params.set('post_logout_redirect_uri', this.#postLogoutRedirectUri);
    }
    this.#pending = true;
    this.#dispatchChange();
    window.location.assign(url);
  }

  /**
   * Initiate the authorization code flow.
   * @param {string} [returnTo] The URL to return to after login.
   * @return {Promise<void>} A promise that resolves when the sign in flow is initiated.
   */
  async signIn(returnTo) {
    this.#sessionStore.set(
      SessionKey.RETURN_TO,
      returnTo || window.location.href,
    );
    try {
      await this.#authorize(false);
    } catch (err) {
      this.#handleError(asError(err));
    }
  }

  /**
   * Renew the tokens.  This method is called automatically before tokens expire.
   * It is exposed mainly for testing, but can be called manually to force renewal.
   * @return {Promise<void>} A promise that resolves when token renewal is initiated.
   */
  async renew() {
    if (!this.#localStore.get(LocalKey.USER)) {
      return;
    }

    try {
      await this.#authorize(true);
    } catch (err) {
      this.#handleError(asError(err));
    }
  }

  /**
   * @param {number} before Schedule token renewal before this time (in milliseconds since the epoch).
   */
  #scheduleRenew(before) {
    clearTimeout(this.#renewTimeoutId);
    const renewMargin =
      minRenewMargin + Math.random() * (maxRenewMargin - minRenewMargin);
    const delay = Math.max(1, before - Date.now() - renewMargin);
    this.#log.debug('auth: scheduling token renewal', {delay});
    this.#renewTimeoutId = setTimeout(() => this.renew(), delay);
  }

  /**
   * @param {Error} error The error.
   */
  #handleError(error) {
    if (!isTopLevel()) {
      /**
       * @type {ErrorMessage}
       */
      const data = {
        type: 'error',
        message: error.message,
        name: error.name,
        stack: error.stack,
        renew: false,
      };
      if (error instanceof AuthenticationError) {
        data.code = error.code;
        data.renew = error.renew;
      }
      window.top?.postMessage(data, window.location.origin);
      return;
    }
    const returnTo = this.#sessionStore.extract(SessionKey.RETURN_TO);

    this.#sessionStore.clear();
    this.#localStore.delete(LocalKey.USER);
    this.#pending = false;

    // errors during token renewal that require additional user interaction trigger sign in
    if (
      error instanceof AuthenticationError &&
      error.renew &&
      requiresInteraction(error.code)
    ) {
      this.signIn(returnTo);
      return;
    }

    this.#error = error;
    this.#log.error(prefix('error'), error);
    this.#dispatchChange();
  }

  /**
   * @param {boolean} renew Initiate the authorization code flow in the iframe instead of the main window.
   */
  async #authorize(renew) {
    this.#error = null;
    const providerConfig = await this.#getProviderConfig();

    const state = randomBytes();
    this.#sessionStore.set(SessionKey.STATE, state);

    const nonce = randomBytes();
    this.#sessionStore.set(SessionKey.NONCE, nonce);

    const codeVerifier = randomBytes();
    this.#sessionStore.set(SessionKey.CODE_VERIFIER, codeVerifier);

    const codeChallenge = await calculateCodeChallenge(codeVerifier);

    const authorizationUrl = formatAuthenticationRequest(
      providerConfig.authorizationEndpoint,
      {
        clientId: this.#clientId,
        codeChallenge,
        codeChallengeMethod,
        redirectUri: this.#redirectUri,
        responseType: 'code',
        responseMode: 'query',
        scopes: this.#scopes,
        nonce,
        state,
        prompt: renew ? 'none' : undefined,
        extraParams: this.#extraParams,
      },
    );

    this.#log.debug('auth: initiating authorization code flow', {renew});

    if (renew) {
      if (!this.#iframe) {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        this.#iframe = iframe;
      }
      this.#sessionStore.set(SessionKey.AWAITING_RENEW, true);
      this.#iframe.src = authorizationUrl;
    } else {
      this.#sessionStore.set(SessionKey.AWAITING_SIGN_IN, true);
      this.#pending = true;
      this.#dispatchChange();
      window.location.assign(authorizationUrl);
    }
  }

  /**
   * Determine if the configured redirect URI matches the current URL (allowing for
   * extra search parameters in the current URL).
   * @param {string} href The current URL.
   */
  #matchesRedirectUri(href) {
    if (href === this.#redirectUri) {
      return true;
    }

    const current = new URL(href);
    const redirect = new URL(this.#redirectUri);
    if (redirect.origin !== current.origin) {
      return false;
    }
    if (redirect.pathname !== current.pathname) {
      return false;
    }
    if (redirect.search === current.search) {
      return true;
    }

    // allow for extra search parameters in the current URL
    const redirectParams = new URLSearchParams(redirect.search);
    const currentParams = new URLSearchParams(current.search);
    for (const [key, value] of redirectParams) {
      if (currentParams.get(key) !== value) {
        return false;
      }
    }
    return true;
  }

  /**
   * Handle the authorization code redirect.
   * @param {boolean} renew Handling the redirect in an iframe for token renewal.
   */
  async #handleRedirect(renew) {
    const returnTo = this.#sessionStore.extract(SessionKey.RETURN_TO);

    const url = new URL(window.location.href);
    const response = extractAuthenticationResponse(url.searchParams);
    window.history.replaceState(null, '', url);

    if (response.error) {
      throw new AuthenticationError(
        response.error,
        renew,
        response.errorDescription,
      );
    }

    if (!response.code) {
      // this happens if the user navigates back without logging in
      this.#log.warn('auth: redirect without code');
      return;
    }

    const state = this.#sessionStore.extract(SessionKey.STATE);
    if (response.state && response.state !== state) {
      throw new Error('State mismatch in redirect');
    }

    const codeVerifier = this.#sessionStore.extract(SessionKey.CODE_VERIFIER);
    if (!codeVerifier) {
      throw new Error('No code verifier');
    }

    const nonce = this.#sessionStore.extract(SessionKey.NONCE);

    this.#pending = true;
    if (!renew) {
      // notify listeners of pending state
      this.#dispatchChange();
    }
    const tokens = await this.#getTokens(response.code, codeVerifier);
    this.#pending = false;

    const claims = parseIdTokenClaims(tokens.idToken);
    if (nonce && claims.nonce !== nonce) {
      throw new Error('Nonce mismatch');
    }

    /**
     * @type {User}
     */
    const user = {
      claims,
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      expiry: tokens.expiry,
    };

    this.#localStore.set(LocalKey.USER, user);

    if (renew) {
      /**
       * @type {RenewMessage}
       */
      const data = {type: 'renew', expiry: tokens.expiry};
      this.#channel.postMessage(data);
      return;
    }

    this.#scheduleRenew(tokens.expiry);
    this.#dispatchChange();
    if (returnTo && returnTo !== window.location.href) {
      const returnUrl = new URL(returnTo, window.location.href);
      const currentUrl = new URL(window.location.href);
      const relativePath = getRelativeUrl(currentUrl, returnUrl);
      if (relativePath) {
        this.#navigate(relativePath);
      }
    }
  }

  /**
   * @param {MessageEvent<MessageData>} event The message event.
   */
  #handleMessage(event) {
    if (event.origin !== window.location.origin || !event.data) {
      return;
    }

    switch (event.data.type) {
      case 'renew': {
        if (this.#iframe) {
          this.#iframe.remove();
          this.#iframe = null;
        }
        this.#scheduleRenew(event.data.expiry);
        this.#dispatchChange();
        return;
      }
      case 'signOut': {
        this.#signOut();
        return;
      }
      case 'error': {
        /**
         * @type {Error}
         */
        let error;
        if (event.data.name === 'AuthenticationError') {
          error = new AuthenticationError(
            event.data.code || 'unknown',
            event.data.renew,
            event.data.message,
          );
        } else {
          error = new Error(event.data.message);
          error.name = event.data.name;
        }
        error.stack = event.data.stack;
        this.#handleError(error);
        return;
      }
      default:
        this.#log.warn('auth: received unexpected message', event);
    }
  }

  /**
   * @param {string} code The authorization code.
   * @param {string} codeVerifier The PKCE code verifier.
   * @return {Promise<import('./auth.js').TokenResponse>} The token response.
   */
  async #getTokens(code, codeVerifier) {
    const params = formatTokenRequest({
      clientId: this.#clientId,
      code,
      codeVerifier,
      grantType,
      redirectUri: this.#redirectUri,
    });

    const providerConfig = await this.#getProviderConfig();

    this.#log.debug('auth: requesting tokens', Object.fromEntries(params));
    const response = await fetch(providerConfig.tokenEndpoint, {
      method: 'POST',
      body: params,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (!response.ok) {
      throw new Error(
        `Unable to issue token request: ${response.status} ${response.statusText}`,
      );
    }

    return parseTokenResponse(await response.json());
  }

  close() {
    clearTimeout(this.#renewTimeoutId);
    if (this.#iframe) {
      this.#iframe.remove();
    }
    this.#listeners = [];
    this.#channel.close();
  }
}

/**
 * This function should only be called in catch block.  We anticipate that the value
 * thrown is an error, but cannot be sure of it.
 * @param {any} value The value thrown.
 * @return {Error} An error.
 */
function asError(value) {
  if (value instanceof Error) {
    return value;
  }
  return new Error(value);
}
